import { useHistory, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import pick from 'lodash/pick'
import * as Parts from './index'
import * as Yup from 'yup'
import { deleteProductGroup, setProductGroupTranslation, updateProductGroup } from 'app-support/productgroup/state/actions'
import { IProductGroup } from '@cbgms/api'
import { BasicFormLayout, Form } from '@carsys/form'
import { useNotifier } from '@cbgms/base/notifier'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@cbgms/base/http/use-query'
import { productTreeCacheKey } from '../hooks/cache-keys'
import { useGetProductTree } from '../hooks/useGetProductTree'
import { Grid } from '@mui/material'
import { DeleteButton, nonRejectionResult } from '@cbgms/components/delete-flow'
import { Locales } from '@carsys/enums/locales'

export function TopMenu() {
  const { t } = useTranslation()
  const notifier = useNotifier()
  const history = useHistory()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { tree_uuid } = useParams<{ tree_uuid: string }>()
  const { maingroup_uuid } = useParams<{ maingroup_uuid: string }>()
  const { data: productGroup } = useGetProductTree(tree_uuid)

  const mainGroup = productGroup?.Categories.find(caterory => caterory.UUID === maingroup_uuid)
  const initialValues = pick(mainGroup, ['UUID', 'IsActive', 'Name', 'Locale', 'SortOrder'])

  const onSubmit = async (values: Partial<IProductGroup>) => {
    const translation = {
      Locale: values.Locale! as Locales,
      Name: values.Name!
    }

    try {
      await dispatch(updateProductGroup(values.UUID!, values))
      await dispatch(setProductGroupTranslation(values.UUID!, translation))
      queryClient.invalidateQueries(productTreeCacheKey(tree_uuid))
    } catch (e: any) {
      notifier.error(t('global.http_unknown_error'))
      throw new Error(e)
    }
    return values
  }

  const onSubmitSuccess = () => {
    notifier.success(t('global.successfully_saved'))
  }

  const onDelete = async (dryRun: boolean) => {
    try {
      if (dryRun) {
        return nonRejectionResult
      } else {
        return await dispatch(deleteProductGroup(maingroup_uuid))
      }
    } catch (e: any) {
      notifier.error(t('global.http_unknown_error'))
      throw new Error(e)
    }
  }

  const onDeleteSuccess = async () => {
    notifier.success(t('global.successfully_deleted'))
    history.push(`/product-group/${tree_uuid}`)
  }

  const validationSchema = Yup.object().shape({
    Name: Yup.string().required(),
    SortOrder: Yup.number().required(),
    IsActive: Yup.boolean().optional()
  })

  return (
    <Form
      confirmationRequired
      initialValues={initialValues}
      onSubmit={onSubmit}
      onSubmitSuccess={onSubmitSuccess}
      validationSchema={validationSchema}
    >
      <BasicFormLayout
        deleteButton={
          onDelete &&
          onDeleteSuccess && (
            <Grid item>
              <DeleteButton
                handleDelete={onDelete}
                handleDeleteSuccess={onDeleteSuccess}
                deletedItemString={initialValues.Name!}
                scope='maingroup'
              />
            </Grid>
          )
        }
        backButtonProps={{ to: () => `/product-group/${tree_uuid}` }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Parts.Data />
          </Grid>
          <Grid item xs={6}>
            <Parts.SubGroup />
          </Grid>
        </Grid>
      </BasicFormLayout>
    </Form>
  )
}

export default TopMenu
